
import React, { useContext, useEffect, useLayoutEffect, useRef, useState, useMemo } from 'react';
import useGameBase from '../../hooks/useGameBase';
import SocketContext from '../../context/SocketContext';
import SpectatorPerspectiveSelector from './SpectatorPerspectiveSelector';

export default function SetSquareGame({ roomId, gameState, gamePlayers = [], user = {}, isSpectator = false }) {
  const socket = useContext(SocketContext);

  // --- Spectator Perspective logic (modular, opt-in, as in Canasta) ---
  const perspectiveOptions = useMemo(() => {
    if (Array.isArray(gamePlayers) && gamePlayers.length > 0) return gamePlayers;
    if (Array.isArray(gameState?.players) && gameState.players.length > 0) return gameState.players;
    return [];
  }, [gamePlayers, gameState?.players]);

  const [spectatorViewPlayerId, setSpectatorViewPlayerId] = useState(null);
  const perspectivePlayer = useMemo(() => {
    if (!isSpectator || !spectatorViewPlayerId) return null;
    return perspectiveOptions.find((player) => {
      const playerId = player?.userId || player?.socketId || player?.id || player?.username;
      return String(playerId) === String(spectatorViewPlayerId);
    }) || null;
  }, [perspectiveOptions, isSpectator, spectatorViewPlayerId]);

  useEffect(() => {
    if (!isSpectator) {
      setSpectatorViewPlayerId(null);
      return;
    }
    if (!Array.isArray(perspectiveOptions) || perspectiveOptions.length === 0) {
      setSpectatorViewPlayerId(null);
      return;
    }
    const exists = perspectiveOptions.some((player) => {
      const playerId = player?.userId || player?.socketId || player?.id || player?.username;
      return String(playerId) === String(spectatorViewPlayerId);
    });
    if (!exists) {
      const first = perspectiveOptions[0];
      setSpectatorViewPlayerId(first?.userId || first?.socketId || first?.id || first?.username || null);
    }
  }, [perspectiveOptions, isSpectator, spectatorViewPlayerId]);

  const { submitAction, findPlayerById, perspectiveUser, perspectiveIds } = useGameBase({ gameState, gamePlayers, user, socket, roomId, perspectivePlayer });

  const gridSize = Number(gameState?.gridSize) || 3;
  const grid = Array.isArray(gameState?.grid) ? gameState.grid : [];
  const sets = Array.isArray(gameState?.sets) ? gameState.sets : [];
  const scores = gameState?.scores || {};
  const finished = !!gameState?.finished;
  const started = !!gameState?.started;

  // Use the correct socketId for the perspective (player or spectator)
  const socketId = perspectiveUser?.socketId || socket?.id || user?.socketId;

  const [selected, setSelected] = useState(() => new Set());
  const [optimisticRejected, setOptimisticRejected] = useState(null);
  const [submitResult, setSubmitResult] = useState(null); // 'correct' | 'incorrect' | null
  const resultTimerRef = useRef(null);
  const [hovered, setHovered] = useState(null);

  useEffect(() => {
    // if a set was just found BY THIS PLAYER, clear any selections that were part of that set
    if (!sets || sets.length === 0) return;
    const myId = socketId || user?.userId;
    for (const s of sets) {
      if (s && s.foundBy) {
        // Check if THIS player found this set
        let foundByMe = false;
        if (Array.isArray(s.foundBy)) {
          foundByMe = s.foundBy.includes(myId);
        } else {
          foundByMe = s.foundBy === myId;
        }
        
        // Only remove if THIS player found it
        if (foundByMe) {
          try {
            const newSel = new Set(Array.from(selected));
            for (const idx of (s.indices || [])) newSel.delete(Number(idx));
            if (newSel.size !== selected.size) setSelected(newSel);
          } catch (e) {}
        }
      }
    }
  }, [sets, socketId, user?.userId]);

  const toggleCell = (idx) => {
    if (finished) return;
    // allow selecting boxes even if found by another player, but prevent
    // toggling boxes that *you* have found (foundForMe)
    const foundSet = sets.find(s => Array.isArray(s.indices) && s.indices.includes(idx) && s.foundBy);
    let foundForMe = false;
    if (foundSet && foundSet.foundBy) {
      if (Array.isArray(foundSet.foundBy)) {
        foundForMe = foundSet.foundBy.includes(socketId) || (user?.userId && foundSet.foundBy.includes(user.userId));
      } else {
        foundForMe = foundSet.foundBy === socketId || foundSet.foundBy === user?.userId;
      }
    }
    if (foundForMe) return;
    setSelected((s) => {
      const n = new Set(Array.from(s));
      if (n.has(idx)) n.delete(idx);
      else {
        // enforce max selection equal to gridSize
        if (n.size >= gridSize) {
          // silently ignore attempts to select more than gridSize
          return n;
        }
        n.add(idx);
      }
      return n;
    });
  };

  // Send selection to server whenever it changes
  useEffect(() => {
    const arr = Array.from(selected).map(Number);
    if (arr.length > 0) {
      submitAction({ type: 'select', selected: arr }).catch(() => {});
    }
  }, [selected]);

  const handleSubmit = async () => {
    const arr = Array.from(selected).map(Number);
    try {
      const ack = await submitAction({ type: 'submit', selected: arr });
      const rejected = ack && (ack.status && String(ack.status).startsWith('ignored')) || (ack && ack.error) || (ack && ack.correct === false);
      if (rejected) {
        setOptimisticRejected(true);
        setSubmitResult('incorrect');
        if (resultTimerRef.current) clearTimeout(resultTimerRef.current);
        resultTimerRef.current = setTimeout(() => setSubmitResult(null), 2000);
        setTimeout(() => setOptimisticRejected(false), 1200);
      } else {
        // on success the server will broadcast new state; clear local selection
        setSelected(new Set());
        if (ack && ack.correct) {
          setSubmitResult('correct');
        } else {
          setSubmitResult('incorrect');
        }
        if (resultTimerRef.current) clearTimeout(resultTimerRef.current);
        resultTimerRef.current = setTimeout(() => setSubmitResult(null), 2000);
      }
    } catch (e) {
      setOptimisticRejected(true);
      setSubmitResult('incorrect');
      if (resultTimerRef.current) clearTimeout(resultTimerRef.current);
      resultTimerRef.current = setTimeout(() => setSubmitResult(null), 2000);
      setTimeout(() => setOptimisticRejected(false), 1200);
    }
  };

  const handleClear = async () => {
    try {
      await submitAction({ type: 'clear' });
      setSelected(new Set());
    } catch (e) {}
  };

  const handleStart = async () => {
    try {
      await submitAction({ type: 'start' });
    } catch (e) {}
  };

  const renderCell = (cell, i) => {
    const isSelected = selected.has(i);
    // highlight if part of a found set
    const foundSet = sets.find(s => Array.isArray(s.indices) && s.indices.includes(i) && s.foundBy);
    // only treat as found for THIS player if the foundBy includes them (or equals them)
    let foundForMe = false;
    if (foundSet && foundSet.foundBy) {
      if (Array.isArray(foundSet.foundBy)) {
        foundForMe = foundSet.foundBy.includes(socketId) || (user?.userId && foundSet.foundBy.includes(user.userId));
      } else {
        foundForMe = foundSet.foundBy === socketId || foundSet.foundBy === user?.userId;
      }
    }
    const base = 'border p-2 flex flex-col items-center justify-center text-center select-none min-h-10';
    const classes = [base];
    if (isSelected) classes.push('bg-blue-500 text-white');
    else classes.push('bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100');
    if (foundForMe) classes.push('bg-green-600 text-white');
    // previously we highlighted selected boxes red on optimistic rejection;
    // that behavior was removed per UX request.

    // hover only when not selected and not finished (still prevent hovering boxes you found)
    const hoverable = !isSelected && !finished && !foundForMe;
    if (hoverable) classes.push('cursor-pointer');
    else classes.push('cursor-default');

    // apply explicit hover background when this cell is hovered (reliable even
    // if Tailwind hover utilities are not applied due to runtime class generation)
    const isHovered = hovered === i;
    if (hoverable && isHovered) classes.push('bg-indigo-100');

    // use inline style to guarantee hover background (avoids Tailwind variant ordering issues)
    const inlineStyle = {};
    if (hoverable && isHovered) inlineStyle.backgroundColor = '#b99bffff'; // approx bg-indigo-100
    // subtle transition for smoother hover
    inlineStyle.transition = 'background-color 120ms ease';

    // Make each box square by setting aspect-ratio: 1/1
    inlineStyle.aspectRatio = '1 / 1';
    // For older browsers, fallback: set height = width via JS if needed (not implemented here)
    return (
      <div
        key={`cell-${i}`}
        className={classes.join(' ')}
        style={inlineStyle}
        onClick={() => toggleCell(i)}
        onMouseEnter={() => { if (hoverable) setHovered(i); }}
        onMouseLeave={() => { if (hovered === i) setHovered(null); }}
      >
        <AutoFitText text={foundForMe ? '' : String(cell)} min={9} max={18} />
        {/* no owner label shown on boxes */}
      </div>
    );
  };

  function AutoFitText({ text = '', min = 9, max = 18 }) {
    const containerRef = useRef(null);
    const textRef = useRef(null);
    const [fontSize, setFontSize] = useState(max);

    useLayoutEffect(() => {
      const container = containerRef.current;
      const textNode = textRef.current;
      if (!container || !textNode) return undefined;

      let frameId = null;
      const observer = typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => scheduleMeasure())
        : null;

      const fitsAtSize = (size) => {
        textNode.style.fontSize = `${size}px`;
        const fitsWidth = textNode.scrollWidth <= container.clientWidth + 1;
        const fitsHeight = textNode.scrollHeight <= container.clientHeight + 1;
        return fitsWidth && fitsHeight;
      };

      const measure = () => {
        if (!container.clientWidth || !container.clientHeight || !text) {
          setFontSize(max);
          return;
        }

        let low = min;
        let high = max;
        let best = min;

        for (let step = 0; step < 8; step += 1) {
          const mid = Number(((low + high) / 2).toFixed(2));
          if (fitsAtSize(mid)) {
            best = mid;
            low = mid;
          } else {
            high = mid;
          }

          if (Math.abs(high - low) < 0.35) break;
        }

        if (fitsAtSize(max)) best = max;
        textNode.style.fontSize = `${best}px`;
        setFontSize((current) => (Math.abs(current - best) < 0.25 ? current : best));
      };

      function scheduleMeasure() {
        if (frameId) cancelAnimationFrame(frameId);
        frameId = requestAnimationFrame(measure);
      }

      scheduleMeasure();
      if (observer) observer.observe(container);

      return () => {
        if (frameId) cancelAnimationFrame(frameId);
        if (observer) observer.disconnect();
      };
    }, [max, min, text]);

    return (
      <div
        ref={containerRef}
        style={{
          width: '100%',
          minWidth: 0,
          flex: 1,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          paddingTop: 4,
          paddingBottom: 4,
          overflow: 'hidden',
        }}
      >
        <div
          ref={textRef}
          style={{
            width: '100%',
            fontSize: `${fontSize}px`,
            lineHeight: '1.15',
            whiteSpace: 'normal',
            wordBreak: 'normal',
            overflowWrap: 'normal',
            textAlign: 'center',
          }}
        >
          {text}
        </div>
      </div>
    );
  }

  const gridTemplate = {
    gridTemplateColumns: `repeat(${gridSize}, minmax(0, 1fr))`,
    gap: '3px',
  };

  return (
    <div className="p-4 w-full max-w-3xl mx-auto dark:bg-gray-900 bg-white text-gray-900 dark:text-gray-100">
      {/* Spectator selector (if spectator) */}
      {isSpectator && perspectiveOptions.length > 0 && (
        <SpectatorPerspectiveSelector
          players={perspectiveOptions}
          value={spectatorViewPlayerId}
          onChange={setSpectatorViewPlayerId}
          className="mb-4"
        />
      )}

      {!finished && (
        <>
          <div className="grid" style={gridTemplate}>
            {Array.from({ length: gridSize * gridSize }).map((_, i) => renderCell(grid[i] || '', i))}
          </div>

          <div className="mt-2 text-sm text-gray-400 dark:text-gray-500">Find {gridSize} sets of {gridSize} words each.  </div>

          <div className="flex items-center justify-between mb-3 mt-4">
            <div className="flex items-center space-x-2">
              {!started && !finished && !isSpectator && (
                <button className="px-3 py-1 bg-indigo-600 text-white rounded" onClick={handleStart}>Start</button>
              )}
              {!isSpectator && (
                <button className="px-3 py-1 border rounded" onClick={handleClear}>Clear</button>
              )}
              {(() => {
                const canSubmit = selected.size === gridSize && !finished && started && !isSpectator;
                const label = canSubmit ? 'Submit' : `Pick ${gridSize}`;
                return (
                  <button
                    className={
                      'px-3 py-1 rounded ' + (canSubmit ? 'bg-green-600 text-white cursor-pointer' : 'bg-gray-300 text-gray-600 cursor-not-allowed')
                    }
                    onClick={() => { if (canSubmit) handleSubmit(); }}
                    disabled={!canSubmit}
                  >
                    {label}
                  </button>
                );
              })()}
              {/* submit result to the right of the submit button */}
              {submitResult === 'correct' && (
                <div className="ml-2 text-green-600 font-semibold">Correct</div>
              )}
              {submitResult === 'incorrect' && (
                <div className="ml-2 text-red-600 font-semibold">Incorrect</div>
              )}
            </div>
          </div>

          {/* Show current player's found sets directly under their grid */}
          <div className="mt-6 mb-6">
            <div className="text-base font-bold mb-3 tracking-wide uppercase text-gray-700 dark:text-gray-200">{isSpectator ? "Found Sets (Spectating)" : "Your Found Sets"}</div>
            <div className="flex flex-col gap-3 w-full">
              {(() => {
                // Use the perspective socketId for the sets if spectating
                const myId = socketId || user?.userId;
                const mySets = (sets || []).filter(s => {
                  if (!s || !s.foundBy) return false;
                  if (Array.isArray(s.foundBy)) return s.foundBy.includes(myId) || (user?.userId && s.foundBy.includes(user.userId));
                  return s.foundBy === myId || s.foundBy === user?.userId;
                });
                if (!mySets || mySets.length === 0) {
                  return <div className="text-xs italic text-gray-500">No sets found yet.</div>;
                }
                return mySets.map((fs, idx) => (
                  <div
                    key={`myset-${idx}`}
                    className="rounded-2xl border-2 border-emerald-400/70 dark:border-emerald-500/60 bg-emerald-50 dark:bg-emerald-900/30 shadow px-5 py-3 w-full flex flex-col items-start"
                    style={{ boxShadow: '0 2px 8px 0 rgba(16,185,129,0.07)' }}
                  >
                    <div className="text-xs font-extrabold uppercase tracking-wider text-emerald-700 dark:text-emerald-300 mb-1 drop-shadow-sm">
                      {(fs.link || ('Set ' + (idx+1))).toString().toUpperCase()}
                    </div>
                    <div className="text-xs text-gray-800 dark:text-gray-100 font-medium">
                      {(fs.words || []).join(', ')}
                    </div>
                  </div>
                ));
              })()}
            </div>
          </div>
        </>
      )}
      
      {finished && (
        <div className="mt-4 p-3 bg-gray-100 dark:bg-gray-700 rounded">
          <strong>Game finished</strong>
          <div className="mb-6 mt-6 text-sm">All completed sets:</div>
          <div className="flex flex-col gap-3 w-full">
            {(sets || []).filter(s => s && s.foundBy).map((fs, idx) => {
              const owners = Array.isArray(fs.foundBy) ? fs.foundBy : [fs.foundBy];
              const ownerNames = owners.map(o => (findPlayerById(o) ? (findPlayerById(o).username || findPlayerById(o).userId) : o)).join(', ');
              return (
                <div
                  key={`complete-${idx}`}
                  className="rounded-2xl border-2 border-emerald-400/70 dark:border-emerald-500/60 bg-emerald-50 dark:bg-emerald-900/30 shadow px-5 py-3 w-full flex flex-col items-start"
                  style={{ boxShadow: '0 2px 8px 0 rgba(16,185,129,0.07)' }}
                >
                  <div className="text-xs font-extrabold uppercase tracking-wider text-emerald-700 dark:text-emerald-300 mb-1 drop-shadow-sm">
                    {(fs.link || ('Set ' + (idx+1))).toString().toUpperCase()}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Found by: {ownerNames}</div>
                  <div className="text-xs text-gray-800 dark:text-gray-100 font-medium">{(fs.words || []).join(', ')}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

SetSquareGame.Guide = function SetSquareGuide() {
  return (
    <div>
      <h3 className="font-bold mb-2">How to Play Set Square</h3>
      <ul className="list-disc ml-6">
        <li>The game presents a grid of words. Your goal is to find sets of words that share a common theme.</li>
        <li>Click on the words in the grid to select them. Selected words will be highlighted.</li>
        <li>Click again on a word to deselect it.</li>
        <li>Once you believe you have selected a valid set, click the "Submit" button to submit your selection.</li>    
        <li>First player to find all the sets wins the game.</li>
      </ul>
    </div>
  );
};

 